import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { FaSearch, FaFilter, FaBed, FaBath, FaRulerCombined, FaCar, FaLayerGroup, FaDollarSign, FaHome, FaTimes, FaExpand, FaHeart } from 'react-icons/fa'

// ── Dimension formatter ────────────────────────────────────────────────────
function formatDim(ft, inches) {
    if (!ft) return null
    const i = inches ? Math.round(inches) : 0
    return i > 0 ? `${Math.round(ft)}'${i}"` : `${Math.round(ft)}'`
}

// ── Chip button helper ──────────────────────────────────────────────────────
function ChipGroup({ label, icon: Icon, options, value, onChange }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-2">
                {Icon && <Icon className="text-xs text-gray-400" />}
                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{label}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {options.map((opt) => {
                    const active = value === opt.value
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(active ? '' : opt.value)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                                active
                                    ? 'bg-primary text-black border-primary shadow-sm'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                            }`}
                        >
                            {opt.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default function StoreHome() {
    const { i18n } = useTranslation()
    const currentLang = i18n.language
    const isEs = currentLang === 'es'

    const [items, setItems] = useState([])
    const [filteredItems, setFilteredItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [showFilters, setShowFilters] = useState(false)

    // ── Filter state ──────────────────────────────────────────────────────────
    const [likedPlans, setLikedPlans] = useState(() => JSON.parse(localStorage.getItem('rwg_liked_plans') || '[]'))

    const toggleLike = (planNumber, e) => {
        e.preventDefault()
        e.stopPropagation()
        setLikedPlans(prev => {
            const next = prev.includes(planNumber)
                ? prev.filter(p => p !== planNumber)
                : [...prev, planNumber]
            localStorage.setItem('rwg_liked_plans', JSON.stringify(next))
            return next
        })
    }

    const [searchTerm, setSearchTerm] = useState('')
    const [minBeds, setMinBeds] = useState('')
    const [minBaths, setMinBaths] = useState('')
    const [stories, setStories] = useState('')
    const [garages, setGarages] = useState('')
    const [minSqFt, setMinSqFt] = useState('')
    const [maxSqFt, setMaxSqFt] = useState('')
    const [minPrice, setMinPrice] = useState('')
    const [maxPrice, setMaxPrice] = useState('')
    const [styleFilter, setStyleFilter] = useState('')

    // Chip option sets
    const BED_OPTIONS = [
        { value: '1', label: '1+' },
        { value: '2', label: '2+' },
        { value: '3', label: '3+' },
        { value: '4', label: '4+' },
    ]
    const BATH_OPTIONS = [
        { value: '1', label: '1+' },
        { value: '2', label: '2+' },
        { value: '3', label: '3+' },
    ]
    const STORY_OPTIONS = [
        { value: '1', label: isEs ? '1 Piso' : '1 Story' },
        { value: '2', label: isEs ? '2 Pisos' : '2 Stories' },
        { value: '3', label: isEs ? '3+ Pisos' : '3+ Stories' },
    ]
    const GARAGE_OPTIONS = [
        { value: '0', label: isEs ? 'Sin garage' : 'None' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3+' },
    ]

    async function fetchPublishedItems() {
        setLoading(true)
        const { data, error } = await supabase
            .from('store_items')
            .select('*')
            .eq('is_published', true)
            .order('created_at', { ascending: false })

        if (data) {
            setItems(data)
            setFilteredItems(data)
        } else {
            console.error("Error fetching items:", error)
        }
        setLoading(false)
    }

    useEffect(() => {
        fetchPublishedItems()
    }, [])

    // Derive unique style values from loaded items
    const styleOptions = useMemo(() => {
        const seen = new Set()
        items.forEach(item => { if (item.style) seen.add(item.style) })
        return [...seen].sort()
    }, [items])

    // ── Active filter count (badge) ───────────────────────────────────────────
    const activeCount = [searchTerm, minBeds, minBaths, stories, garages, minSqFt, maxSqFt, minPrice, maxPrice, styleFilter]
        .filter(Boolean).length

    // ── Apply Filters ─────────────────────────────────────────────────────────
    useEffect(() => {
        let result = items

        if (searchTerm) {
            const q = searchTerm.toLowerCase()
            result = result.filter(item => {
                const titleEN = (item.title_en || '').toLowerCase()
                const titleES = (item.title_es || '').toLowerCase()
                const plan = (item.plan_number || '').toLowerCase()
                const style = (item.style || '').toLowerCase()
                return titleEN.includes(q) || titleES.includes(q) || plan.includes(q) || style.includes(q)
            })
        }

        if (minBeds) result = result.filter(item => item.beds >= parseInt(minBeds))
        if (minBaths) result = result.filter(item => item.baths >= parseFloat(minBaths))

        if (stories) {
            if (stories === '3') result = result.filter(item => item.stories >= 3)
            else result = result.filter(item => item.stories === parseFloat(stories))
        }

        if (garages) {
            if (garages === '3') result = result.filter(item => item.garages >= 3)
            else result = result.filter(item => item.garages === parseInt(garages))
        }

        if (minSqFt) result = result.filter(item => item.sq_ft >= parseInt(minSqFt))
        if (maxSqFt) result = result.filter(item => item.sq_ft <= parseInt(maxSqFt))
        if (minPrice) result = result.filter(item => item.price >= parseFloat(minPrice))
        if (maxPrice) result = result.filter(item => item.price <= parseFloat(maxPrice))
        if (styleFilter) result = result.filter(item => item.style === styleFilter)

        setFilteredItems(result)
    }, [searchTerm, minBeds, minBaths, stories, garages, minSqFt, maxSqFt, minPrice, maxPrice, styleFilter, items])

    const clearAll = () => {
        setSearchTerm(''); setMinBeds(''); setMinBaths(''); setStories(''); setGarages('')
        setMinSqFt(''); setMaxSqFt(''); setMinPrice(''); setMaxPrice(''); setStyleFilter('')
    }

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price)
    }

    const getLocalizedTitle = (item) => {
        if (isEs && item.title_es) return item.title_es
        return item.title_en || 'House Plan'
    }

    return (
        <div className="pt-24 pb-16 min-h-screen bg-cloud">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">

                {/* Header */}
                <div className="mb-12 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-4 text-4xl font-bold md:text-5xl text-primary"
                    >
                        {isEs ? 'Planos Arquitectónicos' : 'Architectural Plans'}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="mx-auto max-w-2xl text-lg text-ash"
                    >
                        {isEs ? 'Explora nuestra colección de diseños premium, listos para construir y adaptables a tu visión.' : 'Explore our collection of premium, ready-to-build designs tailored to your vision.'}
                    </motion.p>
                </div>

                {/* Search & Filter Bar */}
                <div className="p-4 mb-8 bg-white rounded-2xl shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row">
                        <div className="relative flex-1">
                            <FaSearch className="absolute left-4 top-1/2 text-gray-400 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={isEs ? 'Buscar planos, estilos o características...' : 'Search plans, styles, or features...'}
                                className="py-3 pr-4 pl-12 w-full bg-gray-50 rounded-xl border border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600">
                                    <FaTimes className="text-sm" />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`relative px-6 py-3 rounded-xl border font-medium flex items-center justify-center gap-2 transition-all ${showFilters ? 'text-black bg-primary border-primary' : 'bg-white border-gray-200 text-ink hover:bg-gray-50'}`}
                        >
                            <FaFilter />
                            {isEs ? 'Filtros' : 'Filters'}
                            {activeCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black text-white text-[10px] font-bold flex items-center justify-center">
                                    {activeCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Expandable Filters */}
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div className="pt-4 mt-4 space-y-5 border-t border-gray-100">

                                    {/* Beds + Baths row */}
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <ChipGroup
                                            label={isEs ? 'Habitaciones mín.' : 'Min Bedrooms'}
                                            icon={FaBed}
                                            options={BED_OPTIONS}
                                            value={minBeds}
                                            onChange={setMinBeds}
                                        />
                                        <ChipGroup
                                            label={isEs ? 'Baños mín.' : 'Min Bathrooms'}
                                            icon={FaBath}
                                            options={BATH_OPTIONS}
                                            value={minBaths}
                                            onChange={setMinBaths}
                                        />
                                    </div>

                                    {/* Stories + Garages row */}
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <ChipGroup
                                            label={isEs ? 'Pisos' : 'Stories'}
                                            icon={FaLayerGroup}
                                            options={STORY_OPTIONS}
                                            value={stories}
                                            onChange={setStories}
                                        />
                                        <ChipGroup
                                            label={isEs ? 'Garages' : 'Garages'}
                                            icon={FaCar}
                                            options={GARAGE_OPTIONS}
                                            value={garages}
                                            onChange={setGarages}
                                        />
                                    </div>

                                    {/* Sq Ft + Price range row */}
                                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FaRulerCombined className="text-xs text-gray-400" />
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{isEs ? 'Pies² mín.' : 'Min Sq Ft'}</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={minSqFt}
                                                onChange={(e) => setMinSqFt(e.target.value)}
                                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary"
                                                placeholder={isEs ? 'Cualquiera' : 'Any'}
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FaRulerCombined className="text-xs text-gray-400" />
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{isEs ? 'Pies² máx.' : 'Max Sq Ft'}</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={maxSqFt}
                                                onChange={(e) => setMaxSqFt(e.target.value)}
                                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary"
                                                placeholder={isEs ? 'Cualquiera' : 'Any'}
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FaDollarSign className="text-xs text-gray-400" />
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{isEs ? 'Precio mín.' : 'Min Price'}</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={minPrice}
                                                onChange={(e) => setMinPrice(e.target.value)}
                                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary"
                                                placeholder="$0"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FaDollarSign className="text-xs text-gray-400" />
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{isEs ? 'Precio máx.' : 'Max Price'}</span>
                                            </div>
                                            <input
                                                type="number"
                                                value={maxPrice}
                                                onChange={(e) => setMaxPrice(e.target.value)}
                                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary"
                                                placeholder={isEs ? 'Cualquiera' : 'Any'}
                                            />
                                        </div>
                                    </div>

                                    {/* Style */}
                                    {styleOptions.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FaHome className="text-xs text-gray-400" />
                                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{isEs ? 'Estilo' : 'Style'}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {styleOptions.map(s => (
                                                    <button
                                                        key={s}
                                                        type="button"
                                                        onClick={() => setStyleFilter(styleFilter === s ? '' : s)}
                                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                                                            styleFilter === s
                                                                ? 'bg-primary text-black border-primary shadow-sm'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                                                        }`}
                                                    >
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Footer row */}
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-sm text-ash">
                                            {filteredItems.length} {isEs ? 'planos encontrados' : 'plans found'}
                                        </span>
                                        {activeCount > 0 && (
                                            <button
                                                onClick={clearAll}
                                                className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
                                            >
                                                <FaTimes className="text-xs" />
                                                {isEs ? 'Limpiar filtros' : 'Clear all filters'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Grid */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-12 h-12 rounded-full border-4 animate-spin border-primary border-t-transparent"></div>
                    </div>
                ) : filteredItems.length > 0 ? (
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {filteredItems.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="flex overflow-hidden flex-col bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group hover:shadow-xl"
                            >
                                <Link to={`/store/${item.plan_number}`} className="block relative overflow-hidden aspect-[4/3] bg-gray-100">
                                    {item.image_url ? (
                                        <img
                                            src={item.image_url}
                                            alt={getLocalizedTitle(item)}
                                            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                                        />
                                    ) : (
                                        <div className="flex justify-center items-center w-full h-full text-gray-400">
                                            No Image Available
                                        </div>
                                    )}
                                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-bold text-ink shadow-sm">
                                        Plan {item.plan_number}
                                    </div>
                                    <button
                                        onClick={(e) => toggleLike(item.plan_number, e)}
                                        className="absolute top-4 left-4 z-10 p-2 rounded-full shadow-sm backdrop-blur-sm transition-all bg-white/90 hover:scale-110"
                                    >
                                        <FaHeart className={likedPlans.includes(item.plan_number) ? 'text-red-500' : 'text-gray-300'} />
                                    </button>
                                    {item.style && (
                                        <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-medium text-white">
                                            {item.style}
                                        </div>
                                    )}
                                </Link>

                                <div className="flex flex-col flex-1 p-6">
                                    <div className="flex justify-between items-start mb-2">
                                        <h2 className="text-xl font-bold leading-tight transition-colors text-ink hover:text-primary line-clamp-2">
                                            <Link to={`/store/${item.plan_number}`}>
                                                {getLocalizedTitle(item)}
                                            </Link>
                                        </h2>
                                    </div>

                                    <p className="mb-4 text-2xl font-semibold text-primary">
                                        {formatPrice(item.price)}
                                    </p>

                                    <div className="grid grid-cols-3 gap-x-2 gap-y-3 pb-6 mb-6 text-sm border-b border-gray-100 text-ash">
                                        <div className="flex gap-1.5 items-center">
                                            <FaRulerCombined className="text-gray-400 shrink-0" />
                                            <span className="truncate">{item.sq_ft?.toLocaleString()} {isEs ? 'ft²' : 'ft²'}</span>
                                        </div>
                                        <div className="flex gap-1.5 items-center">
                                            <FaBed className="text-gray-400 shrink-0" />
                                            <span>{item.beds} {isEs ? 'Hab.' : 'Beds'}</span>
                                        </div>
                                        <div className="flex gap-1.5 items-center">
                                            <FaBath className="text-gray-400 shrink-0" />
                                            <span>{item.baths} {isEs ? 'Baños' : 'Baths'}</span>
                                        </div>
                                        <div className="flex gap-1.5 items-center">
                                            <FaCar className="text-gray-400 shrink-0" />
                                            <span>{item.garages} {isEs ? 'Gar.' : 'Gar.'}</span>
                                        </div>
                                        {item.stories > 0 && (
                                            <div className="flex gap-1.5 items-center">
                                                <FaLayerGroup className="text-gray-400 shrink-0" />
                                                <span>{item.stories} {isEs ? (item.stories === 1 ? 'Piso' : 'Pisos') : (item.stories === 1 ? 'St.' : 'St.')}</span>
                                            </div>
                                        )}
                                        {(item.width_ft || item.depth_ft) && (
                                            <div className="flex gap-1.5 items-center">
                                                <FaExpand className="text-gray-400 shrink-0" />
                                                <span className="truncate">{[formatDim(item.width_ft, item.width_in) && `W ${formatDim(item.width_ft, item.width_in)}`, formatDim(item.depth_ft, item.depth_in) && `D ${formatDim(item.depth_ft, item.depth_in)}`].filter(Boolean).join(' × ')}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto">
                                        <Link
                                            to={`/store/${item.plan_number}`}
                                            className="block px-4 py-3 w-full font-medium text-center bg-gray-50 rounded-xl transition-colors duration-200 hover:bg-primary text-ink hover:text-white"
                                        >
                                            {isEs ? 'Ver Detalles' : 'View Details'}
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className="py-20 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
                        <div className="mb-4 text-gray-400">
                            <FaSearch className="mx-auto text-5xl" />
                        </div>
                        <h3 className="mb-2 text-xl font-medium text-ink">
                            {isEs ? 'No se encontraron planos' : 'No plans found'}
                        </h3>
                        <p className="text-ash">
                            {isEs ? 'Intenta ajustar tus filtros de búsqueda.' : 'Try adjusting your search filters.'}
                        </p>
                        <button
                            onClick={clearAll}
                            className="px-6 py-2 mt-6 font-medium rounded-lg transition-colors bg-primary/10 text-primary hover:bg-primary/20"
                        >
                            {isEs ? 'Limpiar Búsqueda' : 'Clear Search'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
